import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronUp, History, Loader2, Search } from 'lucide-react';
import { getEmailApiBaseUrl } from '../../../utils/emailApi';
import LoadingSpinner from '../../common/LoadingSpinner';
import {
  gymMarketingCardInner,
  gymModalInput,
  gymModalLabel,
  gymSocialCard,
  gymSocialInputIcon,
  gymSocialListItem,
  gymSocialListItemButton,
  gymSocialListItemExpanded,
  gymSocialListTitle,
  gymSocialLogMessage,
  gymSocialLogMeta,
  gymSocialLogText,
  gymSocialMutedText,
  gymSocialTitleText,
} from '../gymPageTheme';

const STATUS_CLASS = {
  completed: 'text-emerald-600 dark:text-emerald-400',
  running: 'text-sky-600 dark:text-sky-400',
  waiting: 'text-amber-600 dark:text-amber-400',
  failed: 'text-red-600 dark:text-red-400',
  cancelled: 'text-gray-500 dark:text-white/50',
};

export default function GymEmailAutomationRunsPanel({ api, automations = [] }) {
  const { t } = useTranslation();
  const emailApi = getEmailApiBaseUrl();
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [filterAutomation, setFilterAutomation] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterAutomation) params.set('automation_id', filterAutomation);
      if (search.trim()) params.set('search', search.trim());
      const qs = params.toString();
      const res = await api.get(
        `gym_email_automations.php?action=runs${qs ? `&${qs}` : ''}`,
        { baseURL: emailApi }
      );
      setRuns(res.data?.runs || []);
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.automations.runsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [api, emailApi, filterAutomation, search, t]);

  useEffect(() => {
    const timer = setTimeout(loadRuns, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [loadRuns, search]);

  const toggleExpand = async (runId) => {
    if (expandedId === runId) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(runId);
    setDetailLoading(true);
    try {
      const res = await api.get(`gym_email_automations.php?action=run&id=${runId}`, { baseURL: emailApi });
      setDetail(res.data?.run || null);
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.automations.runsLoadError'));
      setExpandedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatWhen = (value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  return (
    <div className={gymSocialCard}>
      <div className={gymMarketingCardInner}>
        <h2 className={`${gymSocialTitleText} flex items-center gap-2`}>
          <History className="h-5 w-5" />
          {t('gym.email.automations.runsTitle')}
        </h2>
        <p className={`${gymSocialMutedText} mt-1 mb-4`}>{t('gym.email.automations.runsHint')}</p>

        <div className="grid gap-3 sm:grid-cols-2 mb-4">
          <div>
            <label className={gymModalLabel}>{t('gym.email.automations.runsFilterRule')}</label>
            <select
              className={gymModalInput}
              value={filterAutomation}
              onChange={(e) => setFilterAutomation(e.target.value)}
            >
              <option value="">{t('gym.email.automations.runsAllRules')}</option>
              {automations.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={gymModalLabel}>{t('gym.email.automations.runsSearchMember')}</label>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${gymSocialInputIcon}`} />
              <input
                className={`${gymModalInput} pl-9`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('gym.email.automations.runsSearchPlaceholder')}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : runs.length === 0 ? (
          <p className={gymSocialMutedText}>{t('gym.email.automations.runsEmpty')}</p>
        ) : (
          <ul className="space-y-2">
            {runs.map((run) => {
              const open = expandedId === run.id;
              return (
                <li key={run.id} className={gymSocialListItem}>
                  <button
                    type="button"
                    className={gymSocialListItemButton}
                    onClick={() => toggleExpand(run.id)}
                  >
                    <div className="min-w-0">
                      <p className={`truncate ${gymSocialListTitle}`}>{run.automation_name}</p>
                      <p className={`${gymSocialMutedText} text-xs mt-0.5`}>
                        {run.user_name || t('gym.email.automations.runsUnknownMember')}
                        {run.user_email ? ` · ${run.user_email}` : ''}
                      </p>
                      <p className={`${gymSocialMutedText} text-xs mt-1`}>
                        {formatWhen(run.started_at)}
                        {' · '}
                        <span className={STATUS_CLASS[run.status] || 'text-gray-600 dark:text-white/70'}>
                          {t(`gym.email.automations.runStatus.${run.status}`, run.status)}
                        </span>
                        {run.last_error && (
                          <span className="text-red-600 dark:text-red-300/90"> · {run.last_error}</span>
                        )}
                      </p>
                    </div>
                    {open ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
                  </button>
                  {open && (
                    <div className={gymSocialListItemExpanded}>
                      {detailLoading ? (
                        <Loader2 className={`h-5 w-5 animate-spin ${gymSocialInputIcon}`} />
                      ) : detail?.id === run.id ? (
                        <div className="space-y-2">
                          {detail.wait_until && (
                            <p className={`${gymSocialMutedText} text-xs`}>
                              {t('gym.email.automations.runsWaitUntil')}: {formatWhen(detail.wait_until)}
                            </p>
                          )}
                          {(detail.logs || []).length === 0 ? (
                            <p className={gymSocialMutedText}>{t('gym.email.automations.runsNoSteps')}</p>
                          ) : (
                            <ol className="space-y-1.5">
                              {detail.logs.map((log) => (
                                <li
                                  key={log.id}
                                  className={`flex flex-wrap gap-x-2 gap-y-0.5 ${gymSocialLogText}`}
                                >
                                  <span className={gymSocialLogMeta}>{log.step_order || '-'}.</span>
                                  <span className="font-medium">{log.action_type}</span>
                                  <span className={
                                    log.status === 'ok' ? 'text-emerald-600 dark:text-emerald-400'
                                      : log.status === 'failed' ? 'text-red-600 dark:text-red-400'
                                        : 'text-amber-600 dark:text-amber-400'
                                  }>
                                    {log.status}
                                  </span>
                                  {log.message && <span className={gymSocialLogMessage}>- {log.message}</span>}
                                </li>
                              ))}
                            </ol>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
